import React, { useMemo, useState } from 'react';
import { User, Brand, SubmissionData, Campaign } from '../../../types';
import { UserRole, AdminView, SubmissionType } from '../../../enums';
import { ManagerDashboardView } from './ManagerDashboardView';
import { hasPerm } from '../../../utils/permissions';
import { ChevronDown, User as UserIcon, Building2, Mail, Tag, Users, LayoutDashboard, Briefcase, FileText, TrendingUp, CheckCircle2, Clock, AlertCircle } from 'lucide-react';

interface ManagersDashboardViewProps {
  users: User[];
  brands: Brand[];
  submissions: SubmissionData[];
  campaigns: Campaign[];
  currentUser: User;
  onOpenSubmissionDetail?: (id: string, type?: SubmissionType) => void;
}

export const ManagersDashboardView: React.FC<ManagersDashboardViewProps> = ({
  users,
  brands,
  submissions,
  campaigns,
  currentUser,
  onOpenSubmissionDetail,
}) => {
  // Filter to only active managers
  const managers = useMemo(() => {
    return users.filter(
      (u) => u.role === UserRole.MANAGER && u.active === true
    );
  }, [users]);

  const [selectedManagerId, setSelectedManagerId] = useState<string | null>(
    managers.length > 0 ? managers[0].id : null
  );

  const selectedManager = useMemo(() => {
    if (!selectedManagerId) return null;
    return managers.find((m) => m.id === selectedManagerId) || null;
  }, [managers, selectedManagerId]);

  // Brand lookup for displaying brand names
  const brandLookup = useMemo(() => {
    const map = new Map<string, string>();
    brands.forEach((b) => map.set(b.id, b.name));
    return map;
  }, [brands]);

  // Get assigned brand names for selected manager
  const assignedBrandNames = useMemo(() => {
    if (!selectedManager || !selectedManager.brandIds || selectedManager.brandIds.length === 0) {
      return null; // null means "All Brands"
    }
    return selectedManager.brandIds
      .map((id) => brandLookup.get(id) || id)
      .filter(Boolean);
  }, [selectedManager, brandLookup]);

  // Filter submissions for the selected manager
  const filteredSubmissions = useMemo(() => {
    if (!selectedManager) return [];

    // First filter by manager
    const managerSubs = submissions.filter(
      (s) => s.managerId === selectedManager.id || s.manager?.fileNumber === selectedManager.id
    );

    // Then apply brand scope if manager has brandIds
    if (!selectedManager.brandIds || selectedManager.brandIds.length === 0) {
      // No brand restriction - include all
      return managerSubs;
    }

    // Get allowed brand names (case-insensitive)
    const allowedBrandNames = new Set(
      selectedManager.brandIds
        .map((id) => (brandLookup.get(id) || '').toLowerCase())
        .filter(Boolean)
    );

    return managerSubs.filter((s) => {
      // Check if any of the submission's brands match allowed brands
      const subBrands = s.brands || [];
      return subBrands.some((b) => allowedBrandNames.has(b.toLowerCase()));
    });
  }, [selectedManager, submissions, brandLookup]);

  // Stats for selected manager
  const managerStats = useMemo(() => {
    const total = filteredSubmissions.length;
    const items = filteredSubmissions.reduce((acc, s) => acc + (s.items?.length || 0), 0);
    const pending = filteredSubmissions.filter((s) => s.status === 'Pending').length;
    const approved = filteredSubmissions.filter((s) => s.status === 'Approved').length;
    const rejected = filteredSubmissions.filter((s) => s.status === 'Rejected').length;
    return { total, items, pending, approved, rejected };
  }, [filteredSubmissions]);

  // Empty state when no managers
  if (managers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="text-center p-10 bg-white rounded-xl border border-zinc-200 shadow-sm max-w-lg">
          <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-zinc-400" />
          </div>
          <h3 className="text-lg font-semibold text-zinc-900 mb-2">No Managers Found</h3>
          <p className="text-sm text-zinc-500">
            There are no active managers in the system. Create a manager account in User Management first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in w-full max-w-full">
      {/* Header & Controls */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10 -mx-4 md:-mx-10 px-4 md:px-10 py-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 max-w-full">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-teal-600 rounded-lg shadow-sm">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Managers Dashboard</h1>
              <p className="text-xs text-zinc-500">Monitor manager performance and submissions</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative min-w-[320px] group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Users className="h-4 w-4 text-zinc-400 group-hover:text-teal-500 transition-colors" />
              </div>
              <select
                value={selectedManagerId || ''}
                onChange={(e) => setSelectedManagerId(e.target.value || null)}
                className="appearance-none w-full pl-10 pr-10 py-3 bg-white border border-zinc-200 rounded-xl text-sm font-bold text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all hover:border-teal-300 hover:shadow-md cursor-pointer"
              >
                <option value="" disabled>Select Manager...</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.username})
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <div className="bg-zinc-100 rounded-md p-1 group-hover:bg-teal-50 transition-colors">
                  <ChevronDown className="w-4 h-4 text-zinc-500 group-hover:text-teal-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedManager && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar: Profile & Quick Stats */}
          <div className="lg:col-span-3 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
              <div className="p-6 flex flex-col items-center text-center border-b border-zinc-100">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-2xl font-bold text-white mb-3 shadow-md shadow-teal-100">
                  {selectedManager.name.charAt(0).toUpperCase()}
                </div>
                <h2 className="text-lg font-bold text-zinc-900">{selectedManager.name}</h2>
                <p className="text-sm text-zinc-500">@{selectedManager.username}</p>
                <div className="mt-3 inline-flex items-center px-2.5 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium">
                  <Building2 className="w-3 h-3 mr-1.5" />
                  {selectedManager.department || 'No Dept.'}
                </div>
              </div>
              
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </span>
                  <span className="font-medium text-zinc-900 truncate max-w-[140px]" title={selectedManager.email}>
                    {selectedManager.email}
                  </span>
                </div>
                <div className="pt-3 border-t border-zinc-100">
                  <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2 block">Assigned Brands</span>
                  <div className="flex flex-wrap gap-1.5">
                    {assignedBrandNames === null ? (
                      <span className="px-2 py-1 bg-zinc-100 text-zinc-600 rounded text-xs font-medium">All Brands</span>
                    ) : (
                      assignedBrandNames.map((name) => (
                        <span key={name} className="px-2 py-1 bg-zinc-100 text-zinc-600 rounded text-xs font-medium">
                          {name}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* KPI Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm">
                <p className="text-xs text-zinc-500 font-medium mb-1">Total Submissions</p>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold text-zinc-900">{managerStats.total}</span>
                  <FileText className="w-5 h-5 text-teal-500 mb-1" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm">
                <p className="text-xs text-zinc-500 font-medium mb-1">Total Items</p>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold text-zinc-900">{managerStats.items}</span>
                  <Briefcase className="w-5 h-5 text-blue-500 mb-1" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm">
                <p className="text-xs text-zinc-500 font-medium mb-1">Pending</p>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold text-amber-600">{managerStats.pending}</span>
                  <Clock className="w-5 h-5 text-amber-500 mb-1" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm">
                <p className="text-xs text-zinc-500 font-medium mb-1">Approved</p>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold text-emerald-600">{managerStats.approved}</span>
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mb-1" />
                </div>
              </div>
            </div>
          </div>

          {/* Right Content: Dashboard View */}
          <div className="lg:col-span-9">
            <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden min-h-[600px]">
              <div className="border-b border-zinc-200 px-6 py-4 bg-zinc-50/50 flex items-center justify-between">
                <h3 className="font-bold text-zinc-800 flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4 text-zinc-500" />
                  Dashboard View
                </h3>
                <span className="text-xs text-zinc-400">Viewing as Admin</span>
              </div>
              <div className="p-6">
                <ManagerDashboardView
                  submissions={filteredSubmissions}
                  managerId={selectedManager.id}
                  managerName={selectedManager.name}
                  isAdminView={true}
                  adminViewerName={currentUser.name}
                  canCreate={false}
                  canViewHistory={true}
                  onOpenSubmissionDetail={onOpenSubmissionDetail}
                  onExitAdminView={() => setSelectedManagerId(null)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
